import type {
  AdminServiceTypeSummary,
  AdminUserInvitationSummary,
  AdminUserInvitationsResponse,
  AdminUserSummary,
  AdminUsersResponse,
  CreateUserInvitationRequest,
  CreateUserInvitationResponse,
  DvrScopeMode,
  DvrPermissionKey,
  DvrUserRole,
  DvrUserStatus,
  PublicSiteSettings,
  PublicSiteSettingsResponse,
  ServiceTypeSummary,
  UpdateUserAccessRequest,
} from "@friends-church-dvr/contracts";
import { renderAdminConfiguration } from "./admin-configuration";
import { renderAdminOperations } from "./admin-operations";
import { renderAdminCloudflareMedia } from "./admin-cloudflare-media";
import { renderAdminAuditLog } from "./admin-audit-log";
import { renderEventDetail, renderEventLibrary, renderEventPlanSummary } from "./event-library";
import { playbackUrlAt } from "./event-playback-url";
import { formatMediaTimestamp } from "./indexed-media-timestamp";
import { navigationMenu, wireNavigationMenu } from "./navigation-menu";
import { renderAboutPage } from "./software-versions";
import {
  containedIconRect,
  environmentLabel,
  productionPreviewUrl,
  siteDocumentTitle,
  siteIconLinks,
} from "./site-environment";
import "./styles.css";

interface CurrentUser {
  id: string;
  name: string;
  email: string;
  status: DvrUserStatus;
  role: DvrUserRole | null;
  scopeMode: DvrScopeMode;
  serviceTypeIds: string[];
  permissions: DvrPermissionKey[];
  lastLoginAt: string | null;
  createdAt: string;
  receiveAdminNotifications: boolean;
  receiveOperationalAlerts: boolean;
}

const navigationAccess = (user: CurrentUser) => ({
  manageUsers: user.role === "administrator",
  configure: user.role === "administrator",
  operations: user.role === "administrator",
  auditLog: user.role === "administrator",
});

const app = document.querySelector<HTMLDivElement>("#app");

if (!app) {
  throw new Error("Application root is missing");
}

const escapeHtml = (value: string): string =>
  value.replace(
    /[&<>'"]/g,
    (character) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        "'": "&#39;",
        '"': "&quot;",
      })[character] ?? character,
  );

let brand = `
  <a class="brand" href="/" aria-label="Friends Church DVR home">
    <span class="brand-mark" aria-hidden="true">FC</span>
    <span>
      <strong>Friends Church</strong>
      <small>DVR Platform</small>
    </span>
  </a>
`;

let currentEnvironment = "development";

const defaultSiteSettings: PublicSiteSettings = {
  organizationName: "Friends Church",
  siteName: "DVR Platform",
  themeTemplate: "default",
  backgroundColor: "#0D1915",
  fontColor: "#F4F0E7",
  featureColor: "#385E43",
  fontFamily: "DM Sans",
  logoDataUrl: "/assets/friends-church-white-f-logo.png",
  videoCommentsEnabled: true,
  viewingAnalyticsEnabled: false,
};

let currentSiteSettings = defaultSiteSettings;

const squareIconDataUrl = async (
  logoDataUrl: string,
  backgroundColor?: string,
): Promise<string | null> =>
  new Promise((resolve) => {
    const image = new Image();
    image.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = 512;
      canvas.height = 512;
      const context = canvas.getContext("2d");
      if (!context) {
        resolve(null);
        return;
      }
      if (backgroundColor) {
        context.fillStyle = backgroundColor;
        context.fillRect(0, 0, canvas.width, canvas.height);
      }
      const rect = containedIconRect(image.naturalWidth, image.naturalHeight);
      context.drawImage(image, rect.x, rect.y, rect.width, rect.height);
      resolve(canvas.toDataURL("image/png"));
    };
    image.onerror = () => resolve(null);
    image.src = logoDataUrl;
  });

const applySiteSettings = async (
  settings: PublicSiteSettings,
  environment: string,
): Promise<void> => {
  currentEnvironment = environment;
  const root = document.documentElement;
  root.dataset.themeTemplate = settings.themeTemplate;
  root.style.setProperty("--background-color", settings.backgroundColor);
  root.style.setProperty("--font-color", settings.fontColor);
  root.style.setProperty("--feature-color", settings.featureColor);
  root.style.setProperty(
    "--body-font",
    settings.fontFamily === "System"
      ? "system-ui, sans-serif"
      : `"${settings.fontFamily}", ${["Georgia", "Newsreader", "PT Serif"].includes(settings.fontFamily) ? "serif" : "sans-serif"}`,
  );
  const label = environmentLabel(environment);
  document.title = siteDocumentTitle(settings, environment);
  document.head
    .querySelectorAll<HTMLLinkElement>('link[rel="icon"], link[rel="apple-touch-icon"]')
    .forEach((link) => link.remove());
  const [faviconUrl, shortcutIconUrl] = settings.logoDataUrl
    ? await Promise.all([
        squareIconDataUrl(settings.logoDataUrl),
        squareIconDataUrl(settings.logoDataUrl, settings.featureColor),
      ])
    : [null, null];
  for (const icon of siteIconLinks(faviconUrl, shortcutIconUrl)) {
    const link = document.createElement("link");
    link.rel = icon.rel;
    link.href = icon.href;
    document.head.append(link);
  }
  brand = `
    <a class="brand" href="/" aria-label="${escapeHtml(settings.organizationName)} ${escapeHtml(settings.siteName)} home">
      ${settings.logoDataUrl
        ? `<img class="brand-logo${settings.logoDataUrl === "/assets/friends-church-main-logo.png" ? " bundled-logo" : ""}" src="${escapeHtml(settings.logoDataUrl)}" alt="">`
        : '<span class="brand-mark" aria-hidden="true">FC</span>'}
      <span>
        <strong>${escapeHtml(settings.organizationName)}</strong>
        <small>${escapeHtml(settings.siteName)}${label ? ` <span class="preview-label">${label}</span>` : ""}</small>
      </span>
    </a>
  `;
};

const initialize = async (): Promise<void> => {
  let settings = defaultSiteSettings;
  let environment = "production";
  try {
    const response = await fetch("/api/public-settings", { headers: { Accept: "application/json" } });
    if (response.ok) {
      const payload = (await response.json()) as PublicSiteSettingsResponse;
      settings = payload;
      environment = payload.environment;
    }
  } catch {
    // Checked-in defaults keep the login and status pages usable if D1 is unavailable.
  }
  await applySiteSettings(settings, environment);
  currentSiteSettings = settings;
  await route();
};

const logoutForm = `
  <form method="post" action="/auth/logout">
    <button class="text-button" type="submit">Sign out</button>
  </form>
`;

const formatDate = (value: string | null): string => {
  if (!value) return "Never";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Unknown";
  return new Intl.DateTimeFormat("en-US", {
    dateStyle: "medium",
    timeStyle: "short",
    hour12: true,
    timeZone: "America/New_York",
  }).format(date);
};

const responseErrorMessage = async (response: Response): Promise<string> => {
  try {
    const payload = (await response.json()) as {
      error?: { message?: string };
    };
    return payload.error?.message ?? "The request could not be completed.";
  } catch {
    return "The request could not be completed.";
  }
};

const renderLogin = (): void => {
  const error = new URLSearchParams(window.location.search).has("error");
  const previewUrl = productionPreviewUrl(currentEnvironment);
  app.innerHTML = `
    <main class="auth-shell">
      <header class="auth-header">${brand}<span class="environment">Private access</span></header>
      <section class="auth-card">
        <h1>Welcome back.</h1>
        <p class="lede">Use your Planning Center account to continue. DVR permissions are managed separately by a DVR administrator.</p>
        ${
          error
            ? '<p class="auth-notice error-message">Login could not be completed. Please try again.</p>'
            : ""
        }
        <a class="primary-button" href="/auth/login${new URLSearchParams(window.location.search).get("returnTo") ? `?returnTo=${encodeURIComponent(new URLSearchParams(window.location.search).get("returnTo") ?? "")}` : ""}">
          <span>Continue with Planning Center</span>
          <span aria-hidden="true">→</span>
        </a>
        <p class="privacy-note">Planning Center is used only to verify your identity. DVR does not retain Planning Center tokens.</p>
      </section>
      ${previewUrl ? `<footer class="auth-footer"><a href="${previewUrl}">Launch Preview Test Site</a></footer>` : ""}
    </main>
  `;
};

const renderSharedVideo = async (token: string): Promise<void> => {
  app.innerHTML = `<main class="shell library-shell shared-video-shell"><header class="topbar page-title-topbar">${brand}</header><section class="event-detail" id="shared-video"><p class="lede">Loading your shared video…</p></section></main>`;
  const panel = document.querySelector<HTMLElement>("#shared-video");
  const response = await fetch(`/api/video-invitations/${encodeURIComponent(token)}`, { headers: { Accept: "application/json" } });
  if (!response.ok) { if (panel) panel.innerHTML = "<h1>Video unavailable.</h1><p class=\"lede\">This Custom Share Link is unavailable, expired, or has been revoked.</p>"; return; }
  const video = await response.json() as { videoId: string; title: string; scheduledStartAt: string; playerUrl: string | null; restoreRequired: boolean; markers: Array<{ label: string; startMs: number }> };
  if (panel) {
    panel.innerHTML = `<h1 class="event-detail-title">${escapeHtml(video.title)}</h1><p class="lede">${escapeHtml(formatDate(video.scheduledStartAt))}</p><div class="event-playback-layout ${video.markers.length ? "has-indexed-media" : ""}"><div class="event-player-column">${video.playerUrl ? `<div class="stream-player"><iframe src="${escapeHtml(video.playerUrl)}" title="${escapeHtml(video.title)} video player" allow="autoplay; encrypted-media; picture-in-picture" allowfullscreen></iframe></div>` : `<div class="player-placeholder"><span>${video.restoreRequired ? "Video is being restored" : "Playback unavailable"}</span><small>${video.restoreRequired ? "Please return in a few minutes." : "Please try again later."}</small></div>`}</div>${video.markers.length ? `<aside class="event-marker-container shared-marker-container"><section class="marker-panel" aria-label="Video Sections"><h2>Video Sections</h2><ol class="marker-list">${video.markers.map((marker) => `<li class="indexed-marker"><button class="timestamp-button" type="button" data-seek-ms="${marker.startMs}" aria-label="Play ${escapeHtml(marker.label)} from ${formatMediaTimestamp(marker.startMs)}">${formatMediaTimestamp(marker.startMs)}</button><span class="indexed-marker-name">${escapeHtml(marker.label)}</span></li>`).join("")}</ol></section></aside>` : ""}</div><p class="privacy-note">This Custom Share Link expires after 30 days. Anyone with this link can view this video until it expires or is revoked.</p>`;
    panel.querySelectorAll<HTMLButtonElement>("[data-seek-ms]").forEach((button) => button.addEventListener("click", () => {
      const iframe = panel.querySelector<HTMLIFrameElement>(".stream-player iframe");
      const startMs = Number(button.dataset.seekMs);
      if (iframe && Number.isInteger(startMs) && startMs >= 0) iframe.src = playbackUrlAt(iframe.src, startMs, true);
    }));
  }
};

const getCurrentUser = async (): Promise<CurrentUser> => {
  const response = await fetch("/api/me", {
    headers: { Accept: "application/json" },
  });

  if (response.status === 401) {
    window.location.replace("/login");
    throw new Error("Authentication required");
  }
  if (response.status === 403) {
    window.location.replace("/disabled");
    throw new Error("Account unavailable");
  }
  if (!response.ok) throw new Error("Account status could not be loaded");

  const payload = (await response.json()) as { user: CurrentUser };
  return payload.user;
};

const renderPending = async (): Promise<void> => {
  app.innerHTML = `
    <main class="auth-shell">
      <header class="auth-header">${brand}${logoutForm}</header>
      <section class="auth-card status-card">
        <span class="status-icon" aria-hidden="true">⌛</span>
        <h1>Approval pending.</h1>
        <p class="lede">Your identity is verified. A DVR administrator still needs to assign your role and Service Type access.</p>
        <dl class="identity-details" id="identity-details">
          <div><dt>Status</dt><dd>Checking…</dd></div>
        </dl>
        <button class="secondary-button" id="check-status" type="button">Check status again</button>
      </section>
    </main>
  `;

  const load = async (): Promise<void> => {
    const user = await getCurrentUser();
    if (user.status === "active" && user.role) {
      window.location.replace("/");
      return;
    }

    const details = document.querySelector<HTMLElement>("#identity-details");
    if (details) {
      details.innerHTML = `
        <div><dt>Name</dt><dd>${escapeHtml(user.name)}</dd></div>
        <div><dt>Email</dt><dd>${escapeHtml(user.email)}</dd></div>
        <div><dt>Status</dt><dd>${user.status === "pending" ? "Awaiting approval" : "Setup incomplete"}</dd></div>
      `;
    }
  };

  document
    .querySelector<HTMLButtonElement>("#check-status")
    ?.addEventListener("click", () => void load());
  await load();
};

const renderDisabled = (): void => {
  app.innerHTML = `
    <main class="auth-shell">
      <header class="auth-header">${brand}</header>
      <section class="auth-card status-card">
        <span class="status-icon blocked" aria-hidden="true">×</span>
        <h1>Access disabled.</h1>
        <p class="lede">This account cannot access the DVR platform. Contact a Friends Church DVR administrator if you believe this is a mistake.</p>
        ${logoutForm}
      </section>
    </main>
  `;
};

const renderMyProfile = async (user: CurrentUser): Promise<void> => {
  app.innerHTML = `
    <main class="shell">
      <header class="topbar page-title-topbar">
        ${brand}
        <div class="mobile-page-heading" aria-hidden="true"><h1>My Profile.</h1></div>
        ${navigationMenu(navigationAccess(user))}
      </header>

      <section class="hero page-title-intro">
        <h1>My Profile.</h1>
        <p class="lede">Review your DVR identity, role, and authorized Service Types.</p>
      </section>

      <section aria-labelledby="profile-identity-title">
        <div class="section-heading admin-heading">
          <div>
            <p class="eyebrow">Identity and authorization</p>
            <h2 id="profile-identity-title">Account details</h2>
          </div>
        </div>
        <article class="user-card profile-identity-card">
          <div class="user-card-heading">
            <div>
              <div class="user-name-row">
                <h2>${escapeHtml(user.name)}</h2>
                <span class="user-status ${user.status}">${statusLabel(user.status)}</span>
              </div>
              <p>${escapeHtml(user.email)}</p>
            </div>
            <span class="current-user">Current user</span>
          </div>
          <dl class="user-metadata">
            <div><dt>Current role</dt><dd>${escapeHtml(roleLabel(user.role))}</dd></div>
            <div><dt>Last login</dt><dd>${escapeHtml(formatDate(user.lastLoginAt))}</dd></div>
            <div><dt>Joined</dt><dd>${escapeHtml(formatDate(user.createdAt))}</dd></div>
          </dl>
        </article>
        ${user.role === "administrator" ? `
          <article class="user-card profile-notification-card">
            <form id="admin-notification-preference-form">
              <label class="config-toggle">
                <input type="checkbox" name="receiveAdminNotifications" ${user.receiveAdminNotifications ? "checked" : ""}>
                <span><strong>User access notifications</strong><small>Email me when a new user requests DVR access.</small></span>
              </label>
              <label class="config-toggle">
                <input type="checkbox" name="receiveOperationalAlerts" ${user.receiveOperationalAlerts ? "checked" : ""}>
                <span><strong>Operational alerts</strong><small>Email me when a provider asset needs attention.</small></span>
              </label>
              <div class="profile-actions">
                <button class="secondary-button compact" type="submit">Save User Preferences</button>
                <span class="form-message" role="status" aria-live="polite"></span>
              </div>
            </form>
          </article>
        ` : ""}
      </section>

      <section class="services" aria-labelledby="services-title">
        <div class="section-heading">
          <div>
            <p class="eyebrow">Authorized scope</p>
            <h2 id="services-title">Service Types</h2>
          </div>
          <span id="service-count">—</span>
        </div>
        <div class="service-grid" id="service-grid">
          <article class="service-card skeleton"></article>
          <article class="service-card skeleton"></article>
          <article class="service-card skeleton"></article>
        </div>
      </section>
    </main>
  `;
  wireNavigationMenu();

  document.querySelector<HTMLFormElement>("#admin-notification-preference-form")?.addEventListener("submit", (event) => {
    event.preventDefault();
    const form = event.currentTarget as HTMLFormElement;
    const button = form.querySelector<HTMLButtonElement>('button[type="submit"]');
    const message = form.querySelector<HTMLElement>(".form-message");
    const receiveAdminNotifications = (form.elements.namedItem("receiveAdminNotifications") as HTMLInputElement).checked;
    const receiveOperationalAlerts = (form.elements.namedItem("receiveOperationalAlerts") as HTMLInputElement).checked;
    void (async () => {
      try {
        if (button) button.disabled = true;
        if (message) { message.textContent = "Saving…"; message.className = "form-message visible"; }
        const response = await fetch("/api/me", {
          method: "PATCH",
          headers: { Accept: "application/json", "Content-Type": "application/json" },
          body: JSON.stringify({ receiveAdminNotifications, receiveOperationalAlerts }),
        });
        if (!response.ok) throw new Error("User preferences could not be saved.");
        if (message) { message.textContent = "User preferences saved."; message.className = "form-message visible"; }
      } catch (error) {
        if (message) { message.textContent = error instanceof Error ? error.message : "User preferences could not be saved."; message.className = "form-message visible error-message"; }
      } finally {
        if (button) button.disabled = false;
      }
    })();
  });

  const serviceGrid = document.querySelector<HTMLElement>("#service-grid");
  const serviceCount = document.querySelector<HTMLElement>("#service-count");

  try {
    const serviceTypesResponse = await fetch("/api/service-types", {
      headers: { Accept: "application/json" },
    });

    if (!serviceTypesResponse.ok) {
      throw new Error("Authorized Service Types could not be loaded");
    }

    const serviceTypes = (await serviceTypesResponse.json()) as {
      serviceTypes: ServiceTypeSummary[];
    };

    if (serviceCount) {
      serviceCount.textContent = `${serviceTypes.serviceTypes.length} authorized`;
    }

    if (serviceGrid) {
      serviceGrid.innerHTML =
        serviceTypes.serviceTypes.length === 0
          ? '<p class="empty-state">No Service Types are assigned to this account.</p>'
          : serviceTypes.serviceTypes
              .map(
                (serviceType, index) => `
                  <article class="service-card">
                    <span class="card-number">${String(index + 1).padStart(2, "0")}</span>
                    <h3>${escapeHtml(serviceType.name)}</h3>
                    <p>${escapeHtml(serviceType.slug)}</p>
                    <span class="card-state">Authorized</span>
                  </article>
                `,
              )
              .join("");
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : "Unknown error";
    if (serviceGrid) serviceGrid.innerHTML = `<p class="error-message">${escapeHtml(message)}</p>`;
  }
};

const roleLabel = (role: DvrUserRole | null): string => {
  if (!role) return "Not assigned";
  return role.charAt(0).toUpperCase() + role.slice(1);
};

const statusLabel = (status: DvrUserStatus): string =>
  ({ pending: "Pending", active: "Active", disabled: "Disabled" })[status];

const renderServiceTypeChoices = (
  serviceTypes: AdminServiceTypeSummary[],
  selectedIds: string[],
): string =>
  serviceTypes
    .map(
      (serviceType) => `
        <label class="scope-option${serviceType.isActive ? "" : " inactive"}">
          <input
            type="checkbox"
            name="serviceTypeId"
            value="${escapeHtml(serviceType.id)}"
            ${selectedIds.includes(serviceType.id) ? "checked" : ""}
          >
          <span class="scope-option-name">${escapeHtml(serviceType.name)}${serviceType.isActive ? "" : " (Inactive)"}</span>
        </label>
      `,
    )
    .join("");

const renderUserCard = (
  user: AdminUserSummary,
  serviceTypes: AdminServiceTypeSummary[],
): string => {
  const editableRole = user.role ?? "viewer";
  const editableScopeMode =
    editableRole === "administrator" ? "all" : user.scopeMode;
  const primaryAction =
    user.status === "pending"
      ? "Approve"
      : user.status === "disabled"
        ? "Reactivate"
        : "Save";
  const serviceTypeSummary = editableScopeMode === "all"
    ? "All Service Types"
    : "Selected Service Types";

  if (user.isCurrentUser) return `
    <div class="user-access-row current" data-status="${user.status}">
      <div class="user-identity"><strong>${escapeHtml(user.name)}</strong><small>${escapeHtml(user.email)}</small></div>
      <div>${escapeHtml(roleLabel(user.role))}</div>
      <div>All Service Types</div>
      <div><span class="user-status ${user.status}">${statusLabel(user.status)}</span><small>Last login ${escapeHtml(formatDate(user.lastLoginAt))}</small></div>
      <div class="row-actions"><span class="current-user">Current user</span></div>
    </div>`;

  return `
    <form class="user-access-form user-access-row" data-user-id="${escapeHtml(user.id)}" data-status="${user.status}">
      <div class="user-identity"><strong>${escapeHtml(user.name)}</strong><small>${escapeHtml(user.email)}</small></div>
      <label><span>Role</span><select name="role">
        ${(["viewer", "editor", "manager", "administrator"] as const).map((role) =>
          `<option value="${role}" ${editableRole === role ? "selected" : ""}>${roleLabel(role)}</option>`).join("")}
      </select></label>
      <div class="scope-cell">
        <label><span>Scope</span><select name="scopeMode">
          <option value="selected" ${editableScopeMode === "selected" ? "selected" : ""}>Selected Service Types</option>
          <option value="all" ${editableScopeMode === "all" ? "selected" : ""}>All Service Types</option>
        </select></label>
        <details class="scope-picker"><summary><span>${escapeHtml(serviceTypeSummary)}</span><small>Change</small></summary>
          <fieldset class="scope-options"><legend class="sr-only">Service Types</legend>${renderServiceTypeChoices(
            serviceTypes,
            editableScopeMode === "all" ? serviceTypes.map((serviceType) => serviceType.id) : user.serviceTypeIds,
          )}</fieldset>
        </details>
      </div>
      <div class="user-state"><span class="user-status ${user.status}">${statusLabel(user.status)}</span><small>Last login ${escapeHtml(formatDate(user.lastLoginAt))}</small></div>
      <div class="row-actions">
        <button class="primary-button compact" type="submit">${primaryAction}</button>
        ${user.status === "active" ? `<button class="row-icon-button" type="button" data-action="disable" aria-label="Disable access for ${escapeHtml(user.name)}" title="Disable access">
          <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="8.5"></circle><path d="M6 6l12 12"></path></svg>
        </button>` : ""}
        <button class="row-icon-button danger" type="button" data-action="delete" aria-label="Delete ${escapeHtml(user.name)}" title="Permanently delete user">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h16M9 7V4h6v3m3 0-1 13H7L6 7m4 4v5m4-5v5"></path></svg>
        </button>
      </div>
      <p class="form-message" role="status" aria-live="polite"></p>
    </form>`;
};

const renderInvitationForm = (serviceTypes: AdminServiceTypeSummary[]): string => `
  <form class="invite-user-panel" id="invite-user-form" hidden>
    <div class="invite-user-heading"><div><h3>Invite a Planning Center user</h3><p>Role and Service Type access are applied when the matching Planning Center account signs in.</p></div>
      <button class="text-button" type="button" data-action="cancel-invite">Close</button></div>
    <div class="invite-fields">
      <label><span>Email</span><input type="email" name="recipientEmail" autocomplete="email" required></label>
      <label><span>Role</span><select name="role">${(["viewer", "editor", "manager", "administrator"] as const).map((role) => `<option value="${role}">${roleLabel(role)}</option>`).join("")}</select></label>
      <label><span>Service Type scope</span><select name="scopeMode"><option value="selected">Selected Service Types</option><option value="all">All Service Types</option></select></label>
    </div>
    <fieldset class="scope-options"><legend>Selected Service Types</legend>${renderServiceTypeChoices(serviceTypes, [])}</fieldset>
    <div class="form-actions"><button class="primary-button compact" type="submit">Send invitation</button></div>
    <p class="form-message" role="status" aria-live="polite"></p>
  </form>`;

const renderInvitationList = (
  invitations: AdminUserInvitationSummary[],
  serviceTypes: AdminServiceTypeSummary[],
): string => {
  if (invitations.length === 0) return "";
  const serviceTypeNames = new Map(serviceTypes.map((serviceType) => [serviceType.id, serviceType.name]));
  return `<section class="user-invitations" aria-labelledby="pending-invitations-title">
    <div class="user-invitations-heading"><div><p class="eyebrow">Awaiting acceptance</p><h3 id="pending-invitations-title">Pending Invitations</h3></div><span>${invitations.length}</span></div>
    <div class="user-invitations-table">
      ${invitations.map((invitation) => {
        const scope = invitation.scopeMode === "all"
          ? "All Service Types"
          : invitation.serviceTypeIds.map((id) => serviceTypeNames.get(id) ?? id).join(", ");
        const delivery = invitation.emailStatus === "sent" ? "Email sent" : invitation.emailStatus === "failed" ? "Email failed" : "Email sending";
        return `<div class="user-invitation-row">
          <div><strong>${escapeHtml(invitation.recipientEmail)}</strong><small>${escapeHtml(delivery)}</small></div>
          <div><span>${escapeHtml(roleLabel(invitation.role))}</span><small>${escapeHtml(scope)}</small></div>
          <div><span>Expires</span><small>${escapeHtml(formatDate(invitation.expiresAt))}</small></div>
          <button class="secondary-button compact" type="button" data-revoke-invitation="${escapeHtml(invitation.id)}" data-recipient-email="${escapeHtml(invitation.recipientEmail)}">Revoke</button>
        </div>`;
      }).join("")}
    </div>
  </section>`;
};

const renderAdminUsers = async (currentUser: CurrentUser): Promise<void> => {
  app.innerHTML = `
    <main class="shell admin-shell">
      <header class="topbar page-title-topbar">
        ${brand}
        <div class="mobile-page-heading" aria-hidden="true"><h1>User access.</h1></div>
        ${navigationMenu(navigationAccess(currentUser))}
      </header>

      <section class="admin-intro page-title-intro">
        <h1>User access.</h1>
        <p class="lede">Approve verified Planning Center identities and control DVR roles and Service Type scope. Every saved change is audited.</p>
      </section>

      <section class="user-access-section" aria-labelledby="users-title">
        <div class="section-heading admin-heading">
          <div>
            <p class="eyebrow">Identity and authorization</p>
            <h2 id="users-title">Users</h2>
          </div>
          <div class="admin-heading-actions"><span id="user-count">Loading…</span><button class="primary-button compact" id="invite-user-toggle" type="button">Invite User</button></div>
        </div>
        <div class="admin-notice" id="admin-notice" role="status" aria-live="polite"></div>
        <div id="invite-user-host"></div>
        <div class="user-list" id="user-list">
          <article class="user-card skeleton"></article>
        </div>
      </section>
    </main>
  `;
  wireNavigationMenu();

  const userList = document.querySelector<HTMLElement>("#user-list");
  const userCount = document.querySelector<HTMLElement>("#user-count");
  const adminNotice = document.querySelector<HTMLElement>("#admin-notice");
  const inviteHost = document.querySelector<HTMLElement>("#invite-user-host");
  const inviteToggle = document.querySelector<HTMLButtonElement>("#invite-user-toggle");

  const setFormState = (form: HTMLFormElement): void => {
    const role = form.elements.namedItem("role") as HTMLSelectElement;
    const scopeMode = form.elements.namedItem("scopeMode") as HTMLSelectElement;
    const scopeOptions = form.querySelector<HTMLFieldSetElement>(".scope-options");
    if (role.value === "administrator") {
      scopeMode.value = "all";
      scopeMode.disabled = true;
    } else {
      scopeMode.disabled = false;
    }
    if (scopeOptions) {
      const hasAllScope = scopeMode.value === "all";
      const summary = form.querySelector<HTMLElement>(".scope-picker summary span");
      if (summary) summary.textContent = hasAllScope ? "All Service Types" : "Selected Service Types";
      if (hasAllScope) {
        scopeOptions.querySelectorAll<HTMLInputElement>('input[name="serviceTypeId"]').forEach((input) => {
          input.checked = true;
        });
      }
      scopeOptions.disabled = hasAllScope;
    }
  };

  const requestFromForm = (
    form: HTMLFormElement,
    status: "active" | "disabled",
  ): UpdateUserAccessRequest => {
    const roleSelect = form.elements.namedItem("role") as HTMLSelectElement;
    const scopeSelect = form.elements.namedItem("scopeMode") as HTMLSelectElement;
    const role = roleSelect.value as DvrUserRole;
    const scopeMode: DvrScopeMode =
      role === "administrator" ? "all" : (scopeSelect.value as DvrScopeMode);
    const serviceTypeIds =
      scopeMode === "selected"
        ? Array.from(
            form.querySelectorAll<HTMLInputElement>(
              'input[name="serviceTypeId"]:checked',
            ),
          ).map((input) => input.value)
        : [];

    if (status === "active" && scopeMode === "selected" && serviceTypeIds.length === 0) {
      throw new Error("Choose at least one Service Type or select all scope.");
    }
    return { status, role, scopeMode, serviceTypeIds };
  };

  const updateAccess = async (
    form: HTMLFormElement,
    status: "active" | "disabled",
  ): Promise<void> => {
    const message = form.querySelector<HTMLElement>(".form-message");
    const buttons = form.querySelectorAll<HTMLButtonElement>("button");
    try {
      const request = requestFromForm(form, status);
      buttons.forEach((button) => {
        button.disabled = true;
      });
      if (message) {
        message.textContent = "Saving…";
        message.className = "form-message";
      }

      const userId = form.dataset.userId;
      if (!userId) throw new Error("The selected user is invalid.");
      const response = await fetch(`/api/admin/users/${encodeURIComponent(userId)}`, {
        method: "PATCH",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(request),
      });
      if (!response.ok) throw new Error(await responseErrorMessage(response));

      if (adminNotice) {
        adminNotice.textContent =
          status === "disabled" ? "User access disabled." : "User access saved.";
        adminNotice.className = "admin-notice visible";
      }
      await loadUsers();
    } catch (error) {
      if (message) {
        message.textContent =
          error instanceof Error ? error.message : "User access could not be saved.";
        message.className = "form-message error-message";
      }
      buttons.forEach((button) => {
        button.disabled = false;
      });
    }
  };

  const deleteAccess = async (form: HTMLFormElement): Promise<void> => {
    const userId = form.dataset.userId;
    if (!userId) return;
    const name = form.querySelector<HTMLElement>(".user-identity strong")?.textContent ?? "this user";
    const confirmation = window.prompt(`Permanently delete ${name} and their DVR access?\n\nType "DELETE" to confirm.`);
    if (confirmation === null) return;
    if (confirmation !== "DELETE") {
      const message = form.querySelector<HTMLElement>(".form-message");
      if (message) { message.textContent = 'Deletion cancelled. Type "DELETE" exactly to confirm.'; message.className = "form-message error-message"; }
      return;
    }
    const buttons = form.querySelectorAll<HTMLButtonElement>("button");
    buttons.forEach((button) => { button.disabled = true; });
    try {
      const response = await fetch(`/api/admin/users/${encodeURIComponent(userId)}`, {
        method: "DELETE", headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify({ confirmation }),
      });
      if (!response.ok) throw new Error(await responseErrorMessage(response));
      if (adminNotice) { adminNotice.textContent = `${name} was permanently deleted.`; adminNotice.className = "admin-notice visible"; }
      await loadUsers();
    } catch (error) {
      const message = form.querySelector<HTMLElement>(".form-message");
      if (message) { message.textContent = error instanceof Error ? error.message : "The user could not be deleted."; message.className = "form-message error-message"; }
      buttons.forEach((button) => { button.disabled = false; });
    }
  };

  const wireForms = (): void => {
    document
      .querySelectorAll<HTMLFormElement>(".user-access-form")
      .forEach((form) => {
        const role = form.elements.namedItem("role") as HTMLSelectElement;
        const scopeMode = form.elements.namedItem("scopeMode") as HTMLSelectElement;
        role.addEventListener("change", () => setFormState(form));
        scopeMode.addEventListener("change", () => setFormState(form));
        form.addEventListener("submit", (event) => {
          event.preventDefault();
          void updateAccess(form, "active");
        });
        form
          .querySelector<HTMLButtonElement>('[data-action="disable"]')
          ?.addEventListener("click", () => void updateAccess(form, "disabled"));
        form.querySelector<HTMLButtonElement>('[data-action="delete"]')
          ?.addEventListener("click", () => void deleteAccess(form));
        setFormState(form);
      });
  };

  const wireInvitation = (): void => {
    const form = document.querySelector<HTMLFormElement>("#invite-user-form");
    if (!form) return;
    const close = (): void => { form.hidden = true; inviteToggle?.focus(); };
    if (inviteToggle) inviteToggle.onclick = () => { form.hidden = false; form.querySelector<HTMLInputElement>('input[name="recipientEmail"]')?.focus(); };
    form.querySelector<HTMLButtonElement>('[data-action="cancel-invite"]')?.addEventListener("click", close);
    const role = form.elements.namedItem("role") as HTMLSelectElement;
    const scopeMode = form.elements.namedItem("scopeMode") as HTMLSelectElement;
    role.addEventListener("change", () => setFormState(form));
    scopeMode.addEventListener("change", () => setFormState(form));
    setFormState(form);
    form.addEventListener("submit", async (event) => {
      event.preventDefault();
      const message = form.querySelector<HTMLElement>(".form-message");
      const buttons = form.querySelectorAll<HTMLButtonElement>("button");
      try {
        const selectedRole = role.value as DvrUserRole;
        const selectedScope: DvrScopeMode = selectedRole === "administrator" ? "all" : scopeMode.value as DvrScopeMode;
        const serviceTypeIds = selectedScope === "selected"
          ? Array.from(form.querySelectorAll<HTMLInputElement>('input[name="serviceTypeId"]:checked')).map((input) => input.value)
          : [];
        if (selectedScope === "selected" && serviceTypeIds.length === 0) throw new Error("Choose at least one Service Type or select all scope.");
        const request: CreateUserInvitationRequest = {
          recipientEmail: (form.elements.namedItem("recipientEmail") as HTMLInputElement).value,
          role: selectedRole, scopeMode: selectedScope, serviceTypeIds,
        };
        buttons.forEach((button) => { button.disabled = true; });
        if (message) { message.textContent = "Creating invitation…"; message.className = "form-message"; }
        const response = await fetch("/api/admin/users/invitations", {
          method: "POST", headers: { Accept: "application/json", "Content-Type": "application/json" }, body: JSON.stringify(request),
        });
        if (!response.ok) throw new Error(await responseErrorMessage(response));
        const payload = await response.json() as CreateUserInvitationResponse;
        if (message) message.textContent = `Invitation emailed to ${payload.invitation.recipientEmail}.`;
        form.reset();
        setFormState(form);
        if (adminNotice) { adminNotice.textContent = `Invitation emailed to ${payload.invitation.recipientEmail}.`; adminNotice.className = "admin-notice visible"; }
        await loadUsers();
      } catch (error) {
        if (message) { message.textContent = error instanceof Error ? error.message : "The invitation could not be created."; message.className = "form-message error-message"; }
      } finally {
        buttons.forEach((button) => { button.disabled = false; });
      }
    });
  };

  const wireInvitationRevocation = (): void => {
    document.querySelectorAll<HTMLButtonElement>("[data-revoke-invitation]").forEach((button) => {
      button.addEventListener("click", () => {
        const invitationId = button.dataset.revokeInvitation;
        const email = button.dataset.recipientEmail ?? "this recipient";
        if (!invitationId || !window.confirm(`Revoke the invitation for ${email}?`)) return;
        void (async () => {
          try {
            button.disabled = true;
            const response = await fetch(`/api/admin/users/invitations/${encodeURIComponent(invitationId)}`, {
              method: "DELETE", headers: { Accept: "application/json" },
            });
            if (!response.ok) throw new Error(await responseErrorMessage(response));
            if (adminNotice) { adminNotice.textContent = `Invitation for ${email} revoked.`; adminNotice.className = "admin-notice visible"; }
            await loadUsers();
          } catch (error) {
            if (adminNotice) {
              adminNotice.textContent = error instanceof Error ? error.message : "The invitation could not be revoked.";
              adminNotice.className = "admin-notice visible error";
            }
            button.disabled = false;
          }
        })();
      });
    });
  };

  const loadUsers = async (): Promise<void> => {
    if (!userList) return;
    try {
      const [usersResponse, invitationsResponse] = await Promise.all([
        fetch("/api/admin/users", { headers: { Accept: "application/json" } }),
        fetch("/api/admin/users/invitations", { headers: { Accept: "application/json" } }),
      ]);
      if (!usersResponse.ok) throw new Error(await responseErrorMessage(usersResponse));
      if (!invitationsResponse.ok) throw new Error(await responseErrorMessage(invitationsResponse));
      const payload = (await usersResponse.json()) as AdminUsersResponse;
      const invitationPayload = (await invitationsResponse.json()) as AdminUserInvitationsResponse;
      if (inviteHost) inviteHost.innerHTML = renderInvitationForm(payload.serviceTypes) + renderInvitationList(invitationPayload.invitations, payload.serviceTypes);
      userList.innerHTML = `<div class="user-access-header" aria-hidden="true"><span>User</span><span>Role</span><span>Service Type access</span><span>Status</span><span>Actions</span></div>` + payload.users
        .map((user) => renderUserCard(user, payload.serviceTypes))
        .join("");
      if (userCount) {
        const pendingCount = payload.users.filter(
          (user) => user.status === "pending",
        ).length;
        userCount.textContent = `${payload.users.length} users · ${pendingCount} pending`;
      }
      wireForms();
      wireInvitation();
      wireInvitationRevocation();
    } catch (error) {
      userList.innerHTML = `<p class="error-message">${escapeHtml(
        error instanceof Error ? error.message : "Users could not be loaded.",
      )}</p>`;
    }
  };

  if (!navigationAccess(currentUser).manageUsers) {
    window.location.replace("/");
    return;
  }
  await loadUsers();
};

const route = async (): Promise<void> => {
  const shareMatch = /^\/shared\/([A-Za-z0-9_-]{20,128})$/u.exec(window.location.pathname);
  if (shareMatch) { await renderSharedVideo(shareMatch[1]); return; }
  switch (window.location.pathname) {
    case "/login":
      renderLogin();
      return;
    case "/pending":
      await renderPending();
      return;
    case "/disabled":
      renderDisabled();
      return;
    case "/admin/users": {
      const user = await getCurrentUser();
      await renderAdminUsers(user);
      return;
    }
    case "/admin/configuration": {
      const user = await getCurrentUser();
      if (user.role !== "administrator") {
        window.location.replace("/");
        return;
      }
      await renderAdminConfiguration({ app, brand, menu: navigationMenu(navigationAccess(user)) });
      wireNavigationMenu();
      return;
    }
    case "/admin/operations": {
      const user = await getCurrentUser();
      if (user.role !== "administrator") { window.location.replace("/"); return; }
      await renderAdminOperations({ app, brand, menu: navigationMenu(navigationAccess(user)) });
      wireNavigationMenu();
      return;
    }
    case "/admin/operations/cloudflare-media": {
      const user = await getCurrentUser();
      if (user.role !== "administrator") { window.location.replace("/"); return; }
      await renderAdminCloudflareMedia({ app, brand, menu: navigationMenu(navigationAccess(user)) });
      wireNavigationMenu();
      return;
    }
    case "/admin/audit-log": {
      const user = await getCurrentUser();
      if (user.role !== "administrator") { window.location.replace("/"); return; }
      await renderAdminAuditLog({ app, brand, menu: navigationMenu(navigationAccess(user)) });
      wireNavigationMenu();
      return;
    }
    case "/":
    case "/library": {
      const user = await getCurrentUser();
      if (user.status === "pending" || !user.role) {
        window.location.replace("/pending");
        return;
      }
      await renderEventLibrary({
        app,
        brand,
        menu: navigationMenu(navigationAccess(user)),
        canManageVisibility: user.permissions.includes("manage_video_visibility"),
        canManageOneTimeEvents: user.permissions.includes("manage_one_time_events"),
      });
      wireNavigationMenu();
      return;
    }
    case "/profile": {
      const user = await getCurrentUser();
      if (user.status === "pending" || !user.role) {
        window.location.replace("/pending");
        return;
      }
      await renderMyProfile(user);
      return;
    }
    case "/about": {
      const user = await getCurrentUser();
      if (user.status === "pending" || !user.role) {
        window.location.replace("/pending");
        return;
      }
      renderAboutPage({ app, brand, menu: navigationMenu(navigationAccess(user)) });
      wireNavigationMenu();
      return;
    }
    default: {
      const user = await getCurrentUser();
      if (user.status === "pending" || !user.role) {
        window.location.replace("/pending");
        return;
      }
      const planSummaryMatch = /^\/events\/([^/]+)\/plan-summary$/u.exec(window.location.pathname);
      if (planSummaryMatch) {
        await renderEventPlanSummary({
          app,
          brand,
          menu: navigationMenu(navigationAccess(user)),
        }, decodeURIComponent(planSummaryMatch[1]));
        wireNavigationMenu();
        return;
      }
      const eventMatch = /^\/events\/([^/]+)$/u.exec(window.location.pathname);
      if (eventMatch) {
        await renderEventDetail({
          app,
          brand,
          menu: navigationMenu(navigationAccess(user)),
          canRestoreArchived: user.permissions.includes("restore_archived_videos"),
          canPlayVideos: user.permissions.includes("play_videos"),
          canManageVisibility: user.permissions.includes("manage_video_visibility"),
          canManageRetention: user.permissions.includes("manage_retention_flags"),
          canEditEventMetadata: user.permissions.includes("edit_event_metadata"),
          canManagePlanAssociation: user.role === "manager" || user.role === "administrator",
          canAdjustCaptureWindows: user.permissions.includes("adjust_capture_windows"),
          canManageOneTimeEvents: user.permissions.includes("manage_one_time_events"),
          canDeleteEvents:
            user.role === "administrator" &&
            user.permissions.includes("permanently_delete_media"),
          canEditPhase1bMetadata: user.permissions.includes("edit_phase1b_metadata"),
          canViewIndexedMedia:
            user.permissions.includes("play_videos") ||
            user.permissions.includes("edit_phase1b_metadata"),
          canShareVideo: user.permissions.includes("share_custom_links"),
          canAddVideoComments: currentSiteSettings.videoCommentsEnabled && user.permissions.includes("add_video_comments"),
          canViewVideoComments: currentSiteSettings.videoCommentsEnabled,
          canViewAnalytics: user.role === "administrator" && currentSiteSettings.viewingAnalyticsEnabled,
        }, decodeURIComponent(eventMatch[1]));
        wireNavigationMenu();
        return;
      }
      window.location.replace("/");
    }
  }
};

void initialize();
